/**
 * Normalizes SSS contribution API rows for the "new format" table.
 * Supports nested employer/employee objects or legacy flat structure.
 */

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).trim();
  if (s === "-" || s.toLowerCase() === "n/a") return null;
  const n = parseFloat(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Grand total from API wrappers */
function grandTotalFromItem(item) {
  const ct = item.contribution_total || item.contributionTotal;
  return (
    num(ct?.total) ??
    num(ct?.Total) ??
    num(item.grand_total) ??
    num(item.grandTotal) ??
    num(item.total_amount) ??
    num(item.total_all)
  );
}

function hasEmployerEmployeeContent(item) {
  const emp = item.employer || item.er;
  const eec = item.employee || item.ee;
  if (emp && typeof emp === "object" && Object.keys(emp).length > 0) return true;
  if (eec && typeof eec === "object" && Object.keys(eec).length > 0) return true;
  return false;
}

export function normalizeSssContributionRow(item) {
  if (!item || typeof item !== "object") {
    return emptyRow();
  }

  const flatEr =
    num(item.employer_regular_ss) ??
    num(item.employer_regular_SS) ??
    num(item.er_regular_ss);
  const flatEe =
    num(item.employee_regular_ss) ??
    num(item.employee_regular_SS) ??
    num(item.ee_regular_ss);
  if (flatEr != null || flatEe != null) {
    const erReg = flatEr;
    const erMpf = num(item.employer_mpf) ?? num(item.er_mpf);
    const erEc = num(item.employer_ec) ?? num(item.er_ec);
    let erTotal = num(item.employer_total) ?? num(item.er_total);
    if (erTotal == null && (erReg != null || erMpf != null || erEc != null)) {
      erTotal = (erReg || 0) + (erMpf || 0) + (erEc || 0);
    }
    const eeReg = flatEe;
    const eeMpf = num(item.employee_mpf) ?? num(item.ee_mpf);
    let eeTotal = num(item.employee_total) ?? num(item.ee_total);
    if (eeTotal == null && (eeReg != null || eeMpf != null)) {
      eeTotal = (eeReg || 0) + (eeMpf || 0);
    }
    let grand = grandTotalFromItem(item);
    if (grand == null && erTotal != null && eeTotal != null) {
      grand = erTotal + eeTotal;
    }
    return {
      range: item.range ?? item.compensation_range ?? item.range_from,
      employerRegularSs: erReg,
      employerMpf: erMpf,
      employerEc: erEc,
      employerTotal: erTotal,
      employeeRegularSs: eeReg,
      employeeMpf: eeMpf,
      employeeTotal: eeTotal,
      grandTotal: grand,
    };
  }

  const emp = item.employer || item.er || {};
  const eec = item.employee || item.ee || {};

  const hasNewShape =
    hasEmployerEmployeeContent(item) ||
    num(emp.reg_ss) != null ||
    num(emp.regular_ss) != null ||
    num(emp.regular_SS) != null ||
    num(eec.reg_ss) != null ||
    num(eec.regular_ss) != null ||
    num(eec.regular_SS) != null;

  if (hasNewShape) {
    const erReg =
      num(emp.reg_ss) ??
      num(emp.regular_ss) ??
      num(emp.regular_SS) ??
      num(emp.regularSs);
    const erMpf = num(emp.mpf) ?? num(emp.MPF);
    const erEc = num(emp.ec) ?? num(emp.EC);
    let erTotal = num(emp.total) ?? num(emp.TOTAL);
    if (erTotal == null && (erReg != null || erMpf != null || erEc != null)) {
      erTotal = (erReg || 0) + (erMpf || 0) + (erEc || 0);
    }

    const eeReg =
      num(eec.reg_ss) ??
      num(eec.regular_ss) ??
      num(eec.regular_SS) ??
      num(eec.regularSs);
    const eeMpf = num(eec.mpf) ?? num(eec.MPF);
    let eeTotal = num(eec.total) ?? num(eec.TOTAL);
    if (eeTotal == null && (eeReg != null || eeMpf != null)) {
      eeTotal = (eeReg || 0) + (eeMpf || 0);
    }

    let grand = grandTotalFromItem(item);
    if (grand == null && erTotal != null && eeTotal != null) {
      grand = erTotal + eeTotal;
    }

    return {
      range: item.range ?? item.compensation_range ?? item.range_from,
      employerRegularSs: erReg,
      employerMpf: erMpf,
      employerEc: erEc,
      employerTotal: erTotal,
      employeeRegularSs: eeReg,
      employeeMpf: eeMpf,
      employeeTotal: eeTotal,
      grandTotal: grand,
    };
  }

  // Legacy: item.sss.{ee,er,total}, item.ec.er, item.total.{ee,er,cont}
  const erSs = num(item.sss?.er);
  const eeSs = num(item.sss?.ee);
  const ec = num(item.ec?.er);
  const erTotal =
    erSs != null || ec != null ? (erSs || 0) + (ec || 0) : null;
  const eeTotal = eeSs;

  let grand = grandTotalFromItem(item) ?? num(item.total?.cont);
  if (grand == null && erTotal != null && eeTotal != null) {
    grand = erTotal + eeTotal;
  }

  return {
    range: item.range,
    employerRegularSs: erSs,
    employerMpf: null,
    employerEc: ec,
    employerTotal: erTotal,
    employeeRegularSs: eeSs,
    employeeMpf: null,
    employeeTotal: eeTotal,
    grandTotal: grand,
  };
}

function emptyRow() {
  return {
    range: null,
    employerRegularSs: null,
    employerMpf: null,
    employerEc: null,
    employerTotal: null,
    employeeRegularSs: null,
    employeeMpf: null,
    employeeTotal: null,
    grandTotal: null,
  };
}
